import {createSlice, PayloadAction} from '@reduxjs/toolkit'
import type {RootState} from '../store/store';
import {AssetsSummary, AssetType} from "./models/Assets";
import {LegalAgreement} from "./models/InvestmentProgram";
import {HoldingAssetSubclassDetails} from "./models/Holding";
import {
    calculateAllAccountsInEstateTotalMarketValue,
    calculateEquityCompensationValue,
    calculateInEstateCNWEquityCompensationTotalValue,
    calculateInEstateEquityCompensationTotalValue,
    calculateInEstateFormValueForLifeInsurance,
    calculatePersonalAssetInEstateTotalValue,
    calculateTotalValue
} from "./AssetSummary/common/AssetSummaryCalculator";

export type ActiveFormAsset = {
    id?: string,
    assetType: AssetType,
    inEstateValue: number,
    description: string,
    hasInEstateOwnership: boolean,
};

export type ClientAssetsState = AssetsSummary & {
    activeFormAsset: ActiveFormAsset | null,
    assetsSummaryLoaded?: boolean,
}

export const defaultAssetsState: ClientAssetsState = {
    accounts: {
        totalPresentValue: 0,
        totalInvestableValue: 0,
        totalRiskControlPresentValue: 0,
        totalInvestableTaxValue: 0,
        totalInvestableTaxDeferredValue: 0,
        taxableRatio: 0,
        totalPresentTaxLiabilityForDeferredAccounts: 0,
        totalPresentTaxLiabilityForTaxableAccounts: 0,
        totalPresentTaxLiabilityForAllAccounts: 0,
        totalPresentTaxLiabilityForDeferredAccountsFundedByPortfolio: 0,
        totalPresentTaxLiabilityForTaxableAccountsFundedByPortfolio: 0,
        totalPresentTaxLiabilityForAllAccountsFundedByPortfolio: 0,
        data: []
    },
    personalAssets: {
        totalPresentValue: 0,
        data: []
    },
    generalInflows: {
        totalPresentValue: 0,
        data: []
    },
    socialSecurities: {
        totalPresentValue: 0,
        data: []
    },
    lifeInsurances: {
        data: []
    },
    equityCompensations: {
        totalPresentValue: 0,
        totalAfterTaxUnVestedValue: 0,
        totalAfterTaxVestedValue: 0,
        data: []
    },
    investmentProgram: null,
    partiallyOwnedLegalAgreements: [],
    personalLiabilities: [],
    totalGrossValue: 0,
    totalNetValue: 0,
    totalPresentValue: 0,
    totalLiabilitiesValue: 0,
    liabilitiesValue: {
        inEstateValue: 0,
        outOfEstateValue: 0,
        totalValue: 0
    },
    activeFormAsset: null,
    assetsSummaryLoaded: false,
    totalTaxLiabilities: {
        totalPresentTaxLiabilityForDeferredAccounts: 0,
        totalPresentTaxLiabilityForAllAccounts: 0,
        totalPresentTaxLiabilityForAllAccountsFundedByPortfolio: 0,
        totalPresentTaxLiabilityForDeferredAccountsFundedByPortfolio: 0,
        totalPresentTaxLiabilityForTaxableAccounts: 0,
        totalPresentTaxLiabilityForTaxableAccountsFundedByPortfolio: 0
    },
    bankingAccounts: []
}

function initialState(): ClientAssetsState {
    return defaultAssetsState;
}

const clientAssetsSlice = createSlice({
    name: 'assets',
    initialState,
    reducers: {
        setClientAssets: (state, action: PayloadAction<AssetsSummary>) => ({
            ...state,
            ...action.payload,
            assetsSummaryLoaded: true,
        }),
        setActiveFormAsset: (state, action: PayloadAction<ActiveFormAsset | null>) => {
            state.activeFormAsset = action.payload;
        },
        updateInvestmentProgramLegalAgreementHoldings: (state, action: PayloadAction<{
            legalAgreementId: string,
            allHoldings: HoldingAssetSubclassDetails[]
        }>) => {
            const legalAgreement = state.investmentProgram?.legalAgreements
                ?.find(agreement => agreement.id === action.payload.legalAgreementId);
            if (!legalAgreement) {
                return;
            }

            if (legalAgreement.holdings) {
                legalAgreement.holdings.allHoldings = action.payload.allHoldings;
                return;
            }

            legalAgreement.holdings = {
                totalMarketValue: null,
                totalInvestableValue: null,
                totalTaxCost: null,
                unrealizedCapitalGainsTax: null,
                deferredTaxLiability: null,
                allHoldings: action.payload.allHoldings
            };
        },
        updateInvestmentProgramLegalAgreement: (state, action: PayloadAction<{
            legalAgreementId: string,
            changes: Partial<Omit<LegalAgreement, 'id' | 'holdings'>>
        }>) => {
            const legalAgreement = state.investmentProgram?.legalAgreements
                ?.find(agreement => agreement.id === action.payload.legalAgreementId);
            if (!legalAgreement) {
                return;
            }
            Object.assign(legalAgreement, action.payload.changes);
        },
        resetClientAssets: () => defaultAssetsState
    }
});

const calculateAssetTotal = (activeFormAsset: ActiveFormAsset | null,
                             assetType: ActiveFormAsset["assetType"],
                             assets: AssetWithTotal[],
                             totalValue: number): number => {
    const activeAsset = activeFormAsset?.assetType === assetType ? activeFormAsset : null;
    let total = totalValue;
    if (activeAsset) {
        total += isNaN(activeAsset.inEstateValue) ? 0 : activeAsset.inEstateValue;
        const isEditingExistingAsset = !!activeAsset.id;
        if (isEditingExistingAsset) {
            const originalAsset = assets.find((asset) => asset.id === activeAsset.id);
            total -= originalAsset ? originalAsset.value : 0;
        }
    }
    return total;
};

export type InEstateAssetTotals = {
    accountTotal: number,
    personalAssetTotal: number,
    futureInflowsTotal: number,
    lifeInsuranceTotal: number,
    equityCompensationTotal: number,
    personalLiabilitiesTotal: number,
    totalNetValue: number,
    totalGrossValue: number,
}

type AssetWithTotal = {
    id: string,
    value: number
}

const getInEstateAssetTotals = (assets: ClientAssetsState): InEstateAssetTotals => {
    const accountTotal = getAccountTotal(assets);
    const personalAssetTotal = getPersonalAssetTotal(assets);
    const liabilitiesTotal = getLiabilitiesTotal(assets);
    const lifeInsuranceTotal = getLifeInsuranceTotal(assets);
    const equityCompensationTotal = getEquityCompensationTotal(assets);
    const futureInflowsTotal = getFutureInflowsTotal(assets);

    return {
        accountTotal,
        personalAssetTotal,
        futureInflowsTotal,
        lifeInsuranceTotal,
        equityCompensationTotal,
        personalLiabilitiesTotal: liabilitiesTotal,
        totalNetValue: (accountTotal + personalAssetTotal + futureInflowsTotal + lifeInsuranceTotal + equityCompensationTotal) - liabilitiesTotal,
        totalGrossValue: (accountTotal + personalAssetTotal + futureInflowsTotal + lifeInsuranceTotal + equityCompensationTotal)
    }
}

const getInEstateCnwAssetTotals = (assets: ClientAssetsState, useDeathBenefitForLifeInsurance = false): InEstateAssetTotals => {
    const accountTotal = getAccountTotal(assets);
    const equityCompensationTotal = getEquityCompensationTotalCNW(assets);
    const personalAssetTotal = getPersonalAssetTotal(assets);
    const liabilitiesTotal = getLiabilitiesTotal(assets);
    const lifeInsuranceTotal = getLifeInsuranceTotal(assets, useDeathBenefitForLifeInsurance);

    return {
        accountTotal,
        personalAssetTotal,
        futureInflowsTotal: 0,
        lifeInsuranceTotal,
        equityCompensationTotal,
        personalLiabilitiesTotal: liabilitiesTotal,
        totalNetValue: (accountTotal + personalAssetTotal + lifeInsuranceTotal + equityCompensationTotal) - liabilitiesTotal,
        totalGrossValue: (accountTotal + personalAssetTotal + lifeInsuranceTotal + equityCompensationTotal)
    }
}

function getAccountTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const allAccountsInEstateBaseTotal = calculateAllAccountsInEstateTotalMarketValue(
        assets.accounts.data,
        assets.investmentProgram,
        assets.partiallyOwnedLegalAgreements,
        assets.bankingAccounts)
    if (assets.activeFormAsset?.assetType === 'partiallyOwnedInvestmentAccount') {
        const partiallyOwnedAccountsForCalculation = assets.partiallyOwnedLegalAgreements.map(account => ({
            id:account.id,
            value: account.marketEstateValue.inEstateValue
        }));
        return calculateAssetTotal(assets.activeFormAsset, 'partiallyOwnedInvestmentAccount', partiallyOwnedAccountsForCalculation, allAccountsInEstateBaseTotal);
    }
    if (assets.activeFormAsset?.assetType === 'investmentProgramHAA' && assets.investmentProgram && assets.investmentProgram.heldAwayAccounts) {
        const heldAwayAccountForCalculation = assets.investmentProgram.heldAwayAccounts.map(account => ({
            id:account.id,
            value: account.holdings.marketValue?.inEstateValue ?? 0
        }));
        return calculateAssetTotal(assets.activeFormAsset, 'investmentProgramHAA', heldAwayAccountForCalculation, allAccountsInEstateBaseTotal);
    }

    if (assets.activeFormAsset?.assetType === 'bankingAccount' && assets.bankingAccounts && assets.bankingAccounts.length > 0) {
        const bankAccountsForCalculation = assets.bankingAccounts.map(bankAccount => ({
            id:bankAccount.id,
            value: bankAccount.holdings ? bankAccount.holdings.marketValue?.inEstateValue || 0 : 0
        }));
        return calculateAssetTotal(assets.activeFormAsset, 'bankingAccount', bankAccountsForCalculation, allAccountsInEstateBaseTotal);
    }

    const accountsForCalculation = assets.accounts.data.map(asset => ({id: asset.id, value: asset.inEstateValue}));
    return calculateAssetTotal(assets.activeFormAsset, 'standaloneAccount', accountsForCalculation, allAccountsInEstateBaseTotal);
}

function getPersonalAssetTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const personalAssetsForCalculation = assets.personalAssets.data.map(asset => ({
        id: asset.id,
        value: asset.inEstateValue
    }));
    const inEstatePersonalAssetTotal = calculatePersonalAssetInEstateTotalValue(assets.personalAssets.data);
    return calculateAssetTotal(assets.activeFormAsset, 'personalAsset', personalAssetsForCalculation, inEstatePersonalAssetTotal);
}

function getLiabilitiesTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const liabilitiesForCalculation = assets.personalLiabilities.map(asset => ({
        id: asset.id,
        value: asset.loanBalanceEstateValue.inEstateValue
    }));
    return calculateAssetTotal(assets.activeFormAsset, 'liability', liabilitiesForCalculation, assets.liabilitiesValue.inEstateValue);
}

function getLifeInsuranceTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }, useDeathBenefitForLifeInsurance = false) {
    const lifeInsurancesForCalculation = assets.lifeInsurances.data
        .map(asset => ({
            id: asset.id,
            value: calculateInEstateFormValueForLifeInsurance(useDeathBenefitForLifeInsurance ? asset.deathBenefitValue : asset.cashValue, asset.memberOwnerships)
        }));
    const lifeInsuranceTotal = calculateTotalValue(assets.lifeInsurances.data, useDeathBenefitForLifeInsurance);

    return calculateAssetTotal(assets.activeFormAsset, 'lifeInsurance', lifeInsurancesForCalculation, lifeInsuranceTotal);
}

function getEquityCompensationTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const equityCompensationsForCalculation = assets.equityCompensations.data
        .map(asset => ({
            id: asset.id,
            value: calculateEquityCompensationValue(asset)
        }));
    const equityCompensationTotal = calculateInEstateEquityCompensationTotalValue(assets.equityCompensations.data);

    return calculateAssetTotal(assets.activeFormAsset, 'equityCompensation', equityCompensationsForCalculation, equityCompensationTotal);
}

function getEquityCompensationTotalCNW(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const equityCompensationsForCalculation = assets.equityCompensations.data
        .map(asset => ({
            id: asset.id,
            value: asset.afterTaxVestedValue || 0
        }));
    const equityCompensationTotal = calculateInEstateCNWEquityCompensationTotalValue(assets.equityCompensations.data);

    return calculateAssetTotal(assets.activeFormAsset, 'equityCompensation', equityCompensationsForCalculation, equityCompensationTotal);
}

function getFutureInflowsTotal(assets: AssetsSummary & { activeFormAsset: ActiveFormAsset | null }) {
    const generalInflowsForCalculation = assets.generalInflows.data.map(asset => ({
        id: asset.id!,
        value: asset.presentValue
    }));
    const socialSecuritiesForCalculation = assets.socialSecurities.data.map(asset => ({
        id: asset.id!,
        value: asset.presentValue
    }));
    const allFutureInflowsForCalculation = generalInflowsForCalculation.concat(socialSecuritiesForCalculation);
    const futureInflowsBaseTotal = assets.generalInflows.totalPresentValue + assets.socialSecurities.totalPresentValue;
    return calculateAssetTotal(assets.activeFormAsset, 'futureInflow', allFutureInflowsForCalculation, futureInflowsBaseTotal);
}

export const {
    setClientAssets,
    resetClientAssets,
    setActiveFormAsset,
    updateInvestmentProgramLegalAgreementHoldings,
    updateInvestmentProgramLegalAgreement,
} = clientAssetsSlice.actions;

export const selectClientAssets = (state: RootState) => state.client.assets;
export const selectAssetsSummaryLoaded = (state: RootState) => state.client.assets?.assetsSummaryLoaded ?? false;

export const selectEquityCompensations = (state: RootState) => state.client.assets?.equityCompensations;

export const selectInEstateAssetTotals = (state: RootState) => getInEstateAssetTotals(state.client.assets!);

export const selectInEstateCnwAssetTotals = (state: RootState) => getInEstateCnwAssetTotals(state.client.assets!);

export const selectInEstateCnwAssetTotalsUsingDeathBenefit = (state: RootState) => getInEstateCnwAssetTotals(state.client.assets!, true);

export const selectActiveFormAsset = (state: RootState) => state.client.assets?.activeFormAsset;

export const selectInvestmentProgramLegalAgreementById = (state: RootState, legalAgreementId: string): LegalAgreement | undefined =>
    state.client.assets?.investmentProgram?.legalAgreements?.find(legalAgreement => legalAgreement.id === legalAgreementId);

export default clientAssetsSlice.reducer;
